import { useMemo } from "react";
import { useCompanyProfiles } from "@/features/categories/hooks";
import type { ProfileQueryRequest, Profile } from "@/services/api/companies";
import type { SortField } from "../columnConfig";
import type { SortDirection } from "./useStaffSorting";

export interface UseStaffDataOptions {
  currentPage: number;
  pageSize: number;
  filters: Partial<ProfileQueryRequest>;
  sortField: SortField | null;
  sortDirection: SortDirection;
}

export interface UseStaffDataReturn {
  profiles: Profile[];
  totalCount: number;
  totalPages: number;
  isLoading: boolean;
  error: Error | null;
}

export function useStaffData({
  currentPage,
  pageSize,
  filters,
  sortField,
  sortDirection,
}: UseStaffDataOptions) {
  const request: ProfileQueryRequest = useMemo(() => {
    const defaultSortField: SortField = "experienceCount";
    const effectiveSortField = sortField || defaultSortField;

    const requestData: ProfileQueryRequest = {
      page: currentPage,
      pageSize,
      sortBy: effectiveSortField,
      sortDirection: sortDirection.toUpperCase() === "ASC" ? "ASC" : "DESC",
    };

    const yearsToMonthsFields: (keyof ProfileQueryRequest)[] = [
      "minF1ExperienceYears",
      "maxF1ExperienceYears",
      "minTotalExperienceYears",
      "maxTotalExperienceYears",
    ];

    const searchTerms = Array.isArray(filters.searchTerm)
      ? filters.searchTerm.filter((t) => t.trim().length >= 2)
      : [];
    const hasValidSearch = searchTerms.length > 0;

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        if (
          (key === "searchTerm" || key === "searchColumn") &&
          !hasValidSearch
        ) {
          return;
        }

        if (key === "searchTerm") {
          requestData.searchTerm = searchTerms;
          return;
        }

        let normalizedValue: string | number | boolean | string[] | undefined =
          typeof value === "string" ? value.trim() || undefined : value;

        // API expects months but UI collects years
        if (
          yearsToMonthsFields.includes(key as keyof ProfileQueryRequest) &&
          typeof normalizedValue === "number"
        ) {
          normalizedValue = normalizedValue * 12;
        }

        if (normalizedValue !== undefined && normalizedValue !== "") {
          requestData[key as keyof ProfileQueryRequest] =
            normalizedValue as never;
        }
      }
    });

    return requestData;
  }, [currentPage, pageSize, filters, sortField, sortDirection]);

  const { data, isLoading, error } = useCompanyProfiles(request, {
    enabled: true,
  });

  const profiles = data?.items || [];
  const totalCount = data?.totalCount || 0;
  const totalPages = data?.totalPages || 1;

  return {
    profiles,
    totalCount,
    totalPages,
    isLoading,
    error: error as Error | null,
  };
}
